import {useCallback, useEffect, useState} from "react";

export type Theme = "light" | "dark";

const THEME_KEY = "siteTheme";

function readSavedTheme(): Theme | null {
    try {
        const saved = localStorage.getItem(THEME_KEY);
        return saved === "light" || saved === "dark" ? saved : null;
    } catch {
        return null;
    }
}

function systemTheme(): Theme {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

// An explicit choice is remembered; until then the site follows the OS setting
export function useTheme(): [Theme, () => void] {
    const [savedTheme, setSavedTheme] = useState<Theme | null>(readSavedTheme);
    const [osTheme, setOsTheme] = useState<Theme>(systemTheme);

    useEffect(() => {
        const query = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => setOsTheme(query.matches ? "dark" : "light");
        query.addEventListener("change", onChange);
        return () => query.removeEventListener("change", onChange);
    }, []);

    const theme = savedTheme ?? osTheme;

    const toggleTheme = useCallback(() => {
        const next: Theme = theme === "dark" ? "light" : "dark";
        setSavedTheme(next);
        try {
            localStorage.setItem(THEME_KEY, next);
        } catch {
            // Remembering the choice is a convenience only
        }
    }, [theme]);

    return [theme, toggleTheme];
}
